import type { PurchaseOrderProgressState } from '@/domain/purchaseOrder/PurchaseOrderLifecycle'
import type { GroupResultStatus } from './actionResult'
import type { RequestGroupOrdersRepository } from './RequestGroupOrders'
import type { GroupAwardRequest, GroupComparisonRead } from './GroupComparisonRead'
import type { GroupMemberResult, GroupQuotationRequest } from './GroupQuotation'
import type { GroupInvoiceResult, GroupInvoicesAssociation } from './groupAssociations'

export interface RequestGroupMember {
  id: number
  societyId: number
  requestNumber: number
}

/** `requestGroup` is null when every line went to one society: the api then creates a normal request. */
export interface RequestGroupCreation {
  requestGroup: string | null
  requests: RequestGroupMember[]
}

export const REQUEST_GROUP_ACTIONS = [
  'approve',
  'request_quotation',
  'close_offers',
  'award',
  'generate_purchase_orders',
  'associate_invoices',
  'cancel',
] as const

export type RequestGroupActionKey = (typeof REQUEST_GROUP_ACTIONS)[number]

export interface RequestGroupAction {
  enabled: boolean
  /** The api's code for why it is not enabled (`not_assignee`, `NO_PERMISSION`, ...; the cancel ones still arrive UPPER case); null when enabled. */
  reason: string | null
}

/** Raw amount; `currency` is an ISO code or null when the document has none. */
export interface CurrencyTotal {
  currency: string | null
  total: number
}

export interface NamedRef {
  id: number
  name: string
}

export interface RequestGroupApprover {
  id: number
  name: string
  avatar: string | null
}

export type BudgetCoverage = 'COVERED' | 'NOT_COVERED' | 'PENDING'

/** `Request.RequestStatus` of the api: a member is one more request, so it moves through the same states. */
export type RequestGroupMemberStatus =
  | 'DRAFT' | 'APPROVING' | 'APPROVED' | 'PARTIALLY_APPROVED' | 'REJECTED' | 'RETURNED_TO_REQUESTER' | 'RETURNED_TO_SELECTOR'
  | 'BUDGET_REVIEW' | 'CUSTOM_FIELD_REVIEW' | 'QUOTATION_SELECTION' | 'QUOTATION_REQUEST' | 'QUOTATIONS_APPROVING'
  | 'RECEPTION_CONFIRMATION' | 'PO_GENERATION' | 'CONTRACT_GENERATION' | 'INVOICE_UPLOAD' | 'PROVIDER_CREATION' | 'CANCELLED'
  | 'ANNULLED' | 'READY_FOR_REVIEW' | 'RENDITION_FINISHED' | 'RENDITION_REJECTED' | 'PROGRAMMED_PAYMENT' | 'PARTIALLY_PROGRAMMED'
  | 'PAYED' | 'PARTIALLY_PAYED' | 'POSTPONED' | 'PROVIDER_PRE_EVALUATION'

export type RequestGroupPurchaseOrderStatus = 'DRAFT' | 'PROVISION' | 'CREATED' | 'CANCELLED' | 'ARCHIVED'

export type RequestGroupInvoiceType =
  | 'INVOICE' | 'SERVICE_FEE' | 'THIRD_PARTY_SERVICE_FEE' | 'INTERNATIONAL_INVOICE' | 'CREDIT_NOTE' | 'DEBIT_NOTE' | 'SALES_RECEIPT' | 'OTHER'

export interface RequestGroupMemberSummary {
  requestId: number
  requestNumber: number | null
  society: NamedRef
  status: RequestGroupMemberStatus
  currentStep: { nodeId: number; type: string; name: string } | null
  pendingApprovers: RequestGroupApprover[]
  /** Header total in the member's accounting currency; empty before awarding. */
  totalsByCurrency: CurrencyTotal[]
  /** Awarded lines in the quoted currency, not converted. */
  awardedByCurrency: CurrencyTotal[]
  inPurchaseOrdersByCurrency: CurrencyTotal[]
  budget: { amount: number | null; currency: string | null; coverage: BudgetCoverage | null }
  availableActions: Record<RequestGroupActionKey, RequestGroupAction>
}

/** What the view and the tab components call a member of the open group. */
export type GroupMember = RequestGroupMemberSummary

/** `GET purchases/request-groups/<uuid>/`: members ordered by society name, then id. */
export interface RequestGroupSummary {
  requestGroup: string
  /** The group's own consecutive within the company; null for a group the api has not numbered. */
  requestGroupNumber?: number | null
  members: RequestGroupMemberSummary[]
  totalsByCurrency: CurrencyTotal[]
  awardedByCurrency: CurrencyTotal[]
  inPurchaseOrdersByCurrency: CurrencyTotal[]
}

export interface RequestGroupPurchaseOrder {
  id: number
  number: string | null
  internalNumber: number | null
  status: RequestGroupPurchaseOrderStatus
  provider: NamedRef
  currency: string | null
  /** Gross. `receivedTotal` and `invoicedTotal` are stored net, so never subtract them from this. */
  total: number
  netTotal: number
  /** Null when the provider has 2+ live orders in that currency, or quoted in another one. */
  awarded: number | null
  receivedTotal: number
  invoicedTotal: number
  paidTotal: number
  pendingReception: number
  pendingInvoicing: number
  receptionState: PurchaseOrderProgressState
  invoicingState: PurchaseOrderProgressState
  receptionIds: number[]
  invoiceIds: number[]
  paymentIds: number[]
  /** The api's own answer: not closed and something left to receive. The tab offers "Recepcionar" only on this. */
  canReceive: boolean
}

export interface RequestGroupReception {
  id: number
  number: number
  date: string
  purchaseOrderId: number | null
  amount: number
}

export interface RequestGroupCreditNote {
  id: number
  number: string | null
  status: string
  currency: string | null
  total: number | null
}

export interface RequestGroupInvoice {
  id: number
  number: string | null
  taxAuthorityUid: string | null
  cufe: string | null
  type: RequestGroupInvoiceType
  status: string
  providerId: number
  purchaseOrderId: number | null
  receptionIds: number[]
  currency: string | null
  total: number | null
  paymentSummary: { status: string | null; paid: number; scheduled: number }
  creditNotes: RequestGroupCreditNote[]
}

export interface RequestGroupPayment {
  id: number
  number: number | null
  status: string
  currency: string | null
  total: number
  programmedPaymentDate: string | null
  paymentDate: string | null
  invoiceLinks: { invoiceId: number; amount: number }[]
  purchaseOrderIds: number[]
}

export type SocietyMismatchType = 'purchase_order' | 'reception' | 'invoice' | 'credit_note' | 'payment'

export interface RequestGroupMemberAssociations {
  request: { id: number; requestNumber: number | null; status: RequestGroupMemberStatus; society: NamedRef & { country: string | null } }
  purchaseOrders: RequestGroupPurchaseOrder[]
  awardedByCurrency: CurrencyTotal[]
  awardedWithoutPurchaseOrder: { provider: NamedRef | null; currency: string | null; awarded: number }[]
  receptions: RequestGroupReception[]
  invoices: RequestGroupInvoice[]
  invoicesWithoutPurchaseOrder: number[]
  payments: RequestGroupPayment[]
  /** Should always be empty; when it is not, the view shows it as an alert instead of hiding it. */
  societyMismatches: { type: SocietyMismatchType; id: number; societyId: number }[]
}

export interface RequestGroupAssociationTotal {
  currency: string | null
  awarded: number
  inPurchaseOrders: number
  received: number
  invoiced: number
  paid: number
}

/** `GET purchases/request-groups/<uuid>/associations/`, read by the request FK so a crossed document shows. */
export interface RequestGroupAssociations {
  requestGroup: string
  members: RequestGroupMemberAssociations[]
  totalsByCurrency: RequestGroupAssociationTotal[]
}

export type RequestGroupActionStatus = GroupResultStatus

/** One member's outcome of a group-wide action; `reason` is a stable api code, null when done. */
export interface RequestGroupActionRow {
  requestId: number
  /** Null when the id is not a member of the group (`not_in_group`). */
  societyId: number | null
  status: RequestGroupActionStatus
  reason: string | null
  /** The api's own sentence for a refusal; null when there is none. */
  detail: string | null
}

export interface RequestGroupActionResult {
  results: RequestGroupActionRow[]
}

/** Every call on `purchases/request-groups/`. The member commands answer one result row per member, never all-or-nothing. */
export interface RequestGroupRepository extends RequestGroupOrdersRepository {
  /** The purchase form's FormData with a society per line, one request per society. All or nothing; the key is suffixed per society by the api. */
  create(form: FormData, idempotencyKey: string): Promise<RequestGroupCreation>
  getSummary(groupId: string): Promise<RequestGroupSummary>
  getAssociations(groupId: string): Promise<RequestGroupAssociations>
  approve(group: string, memberIds: number[], comments?: string): Promise<RequestGroupActionResult>
  /** `reason` is mandatory and is stored on every cancelled member. */
  cancel(group: string, memberIds: number[], reason: string): Promise<RequestGroupActionResult>
  /** The whole group's comparison, merged by the api. */
  getGroupComparison(groupId: string): Promise<GroupComparisonRead>
  /** Flat picks: the api routes each pick to the request that owns the row. */
  awardGroup(groupId: string, request: GroupAwardRequest): Promise<GroupMemberResult[]>
  requestQuotations(group: string, request: GroupQuotationRequest): Promise<GroupMemberResult[]>
  closeOffers(group: string, memberIds: number[], comments: string): Promise<GroupMemberResult[]>
  associateInvoices(groupId: string, body: GroupInvoicesAssociation): Promise<GroupInvoiceResult[]>
}
